/**
 * 従業員一覧。絞り込みとページングはサーバー側(GET /api/hr/employees)に任せる。
 *
 * 一覧テンプレート(`ui/`): ページヘッダー / ビューと条件チップ / 表 / 右パネル。
 * 条件は URL の search params に載せる(戻る・共有できる)。行を押すと `?panel=<id>`
 * で右パネルが開き、「全画面で開く」で `/hr/employees/:id` へ。
 */
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Checkbox, Select, Space, TreeSelect, message } from "antd";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import type { ContactMode, ContactRole, Employee } from "../../lib/api";
import { hasFeature } from "../../lib/features";
import { hasRole } from "../../lib/roles";
import { useApi, useMe } from "../../lib/useApi";
import {
  DataTable,
  FilterBar,
  PageHeader,
  PopClose,
  StatusTag,
  useSearchParam,
  useSearchParamInput,
  useSearchParamsPatch,
  type ConditionDef,
  type DataColumn,
} from "../../ui";
import { EmployeeFormModal } from "./EmployeeForm";
import { EmployeePanel } from "./EmployeePanel";
import { STATUS_FILTERS, employeeStatusTone, statusLabel } from "./constants";
import { errorText, unitOptions, useCustomFields, useHrPerms, useOrgUnits } from "./hooks";
import { useDownload } from "./loanHooks";

/** URL から読む条件。`include_descendants` は所属を指定したときだけ意味を持つ(既定 on)。 */
type Filters = {
  q: string;
  status: string;
  scope: "all" | "direct";
  org_unit_id?: number;
  include_descendants: boolean;
  /** 姓しか入っていない(名が空の)行だけに絞る */
  needs_review: boolean;
  /** 並び順。空 = 並び無し(サーバー既定の社員番号順)。社員番号・氏名の見出しで 昇順 → 降順 → 無し と回る */
  sort: "" | "emp_code" | "name";
  order: "asc" | "desc";
};

/** 絞り込みだけ(Excel 出力にも同じものを渡す)。 */
function filterParams(f: Filters): Record<string, string> {
  return {
    scope: f.scope,
    ...(f.q ? { q: f.q } : {}),
    ...(f.status ? { status: f.status } : {}),
    ...(f.needs_review ? { needs_review: "1" } : {}),
    ...(f.org_unit_id
      ? { org_unit_id: String(f.org_unit_id), include_descendants: String(f.include_descendants) }
      : {}),
  };
}

function toParams(f: Filters, limit: number, offset: number): URLSearchParams {
  return new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    ...filterParams(f),
    ...(f.sort ? { sort: f.sort, order: f.order } : {}),
  });
}

/**
 * 保存ビュー(件数付きのチップ)。在籍・直雇用・要確認の組み合わせを名前で呼ぶ。
 * ビューは一括設定(他を消す)。「直雇用 + 要確認」のような組み合わせは
 * 「条件を追加」の 雇用範囲 / 要確認 で個別に足す(ビューはどれも選択なしになる)。
 */
const VIEWS = [
  { key: "all", label: "すべて", set: { scope: null, status: null, needs_review: null } },
  { key: "direct", label: "直雇用", set: { scope: "direct", status: null, needs_review: null } },
  { key: "on_leave", label: "休職中", set: { scope: null, status: "on_leave", needs_review: null } },
  {
    key: "needs_review",
    label: "氏名が要確認",
    set: { scope: null, status: null, needs_review: "1" },
    tone: "warning" as const,
  },
];

function activeView(f: Filters): string {
  if (f.needs_review) return f.scope === "all" && !f.status ? "needs_review" : "";
  if (f.scope === "direct") return f.status ? "" : "direct";
  if (f.status === "on_leave") return "on_leave";
  return f.status ? "" : "all";
}

/**
 * 電話帳同期(CS1)の個別指定。チェックした従業員を電話帳の配布先・掲載に足す / 除外する。
 * ID管理の機能なので、オプションが有効で idp の operator のときだけ出す(関門は API)。
 */
function useContactsBulk() {
  const api = useApi();
  const me = useMe();
  const qc = useQueryClient();
  const enabled = hasFeature(me.data, "idp.contacts-sync") && hasRole(me.data, "idp:operator");
  const books = useQuery({
    queryKey: ["idp", "contacts", "books"],
    queryFn: api.contactBooks,
    enabled,
  });
  const bookId = books.data?.[0]?.id;
  const write = useMutation({
    mutationFn: (args: { ids: number[]; roles: ContactRole[]; mode: ContactMode; label: string }) =>
      api.setContactMembers(bookId as number, {
        employee_ids: args.ids,
        roles: args.roles,
        mode: args.mode,
      }),
    onSuccess: (_r, args) => {
      void qc.invalidateQueries({ queryKey: ["idp", "contacts"] });
      void message.success(`電話帳: ${args.ids.length} 人を${args.label}した`);
    },
    onError: (e) => void message.error(errorText(e)),
  });
  return { enabled: enabled && bookId !== undefined, write };
}

/** ビューの件数(条件や検索語は掛けない。「全体の中でいくつか」を出す)。 */
const VIEW_COUNT_PARAMS: Record<string, Record<string, string>> = {
  all: { scope: "all" },
  direct: { scope: "direct" },
  on_leave: { scope: "all", status: "on_leave" },
  needs_review: { scope: "all", needs_review: "1" },
};

function useViewCounts(): Record<string, number | undefined> {
  const api = useApi();
  const results = useQueries({
    queries: VIEWS.map((v) => ({
      queryKey: ["hr", "employees", "count", v.key],
      queryFn: () =>
        api.employees(new URLSearchParams({ limit: "1", offset: "0", ...VIEW_COUNT_PARAMS[v.key] })),
      select: (d: { total: number }) => d.total,
      staleTime: 60_000,
    })),
  });
  return Object.fromEntries(VIEWS.map((v, i) => [v.key, results[i].data]));
}

export function EmployeesPage() {
  const api = useApi();
  const { canWrite } = useHrPerms();
  const units = useOrgUnits();
  const listFields = (useCustomFields().data?.items ?? []).filter((f) => f.show_in_list);
  const [sp] = useSearchParams();
  const patch = useSearchParamsPatch();
  const [panelId, setPanelId] = useSearchParam("panel");

  const filters: Filters = {
    q: sp.get("q") ?? "",
    status: sp.get("status") ?? "",
    scope: sp.get("scope") === "direct" ? "direct" : "all",
    // 組織図の箱から `?org_unit_id=` で飛んでくる。その組織で絞った状態で開く
    org_unit_id: Number(sp.get("org_unit_id")) || undefined,
    include_descendants: sp.get("include_descendants") !== "0",
    needs_review: sp.get("needs_review") === "1",
    sort: sp.get("sort") === "emp_code" || sp.get("sort") === "name" ? sp.get("sort") as "emp_code" | "name" : "",
    order: sp.get("order") === "desc" ? "desc" : "asc",
  };

  // 検索語は打鍵ごとに投げない。落ち着いてから URL に載せる(履歴は増やさない)
  const [qInput, setQInput] = useSearchParamInput("q");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [creating, setCreating] = useState(false);
  // 電話 3 列は既定で隠す(列が多くて横に長いので、要るときだけ出す)
  const [showPhones, setShowPhones] = useState(false);
  const download = useDownload();
  const contacts = useContactsBulk();
  const counts = useViewCounts();
  const [selected, setSelected] = useState<number[]>([]);
  const contactAction = (roles: ContactRole[], mode: ContactMode, label: string) =>
    contacts.write.mutate(
      { ids: selected, roles, mode, label },
      { onSuccess: () => setSelected([]) },
    );

  const query = useQuery({
    queryKey: ["hr", "employees", filters, page, pageSize],
    queryFn: () => api.employees(toParams(filters, pageSize, (page - 1) * pageSize)),
  });
  // 検索語は URL に直接書かれる(apply を通らない)ので、条件が変わったらここで 1 ページ目へ
  useEffect(() => {
    setPage(1);
  }, [filters.q, filters.status, filters.scope, filters.org_unit_id, filters.include_descendants, filters.needs_review, filters.sort, filters.order]);

  // 条件の変更は履歴を積まない(replace)。パネル開閉は push(戻るで閉じる)
  const apply = (next: Record<string, string | null>) => {
    setPage(1);
    patch(next, true);
  };
  // 何か 1 つでも絞っているか(ビュー・検索・所属)
  const filtered = activeView(filters) !== "all" || Boolean(filters.q) || Boolean(filters.org_unit_id);
  const clearAll = () => {
    setQInput("");
    apply({ q: null, status: null, scope: null, org_unit_id: null, include_descendants: null, needs_review: null });
  };

  const unitName = (id?: number) =>
    units.data?.items.find((u) => u.id === id)?.name ?? (id ? `#${id}` : "");

  const conditions: ConditionDef[] = [
    {
      key: "scope",
      label: "雇用範囲",
      value: filters.scope === "direct" ? "直雇用のみ" : "",
      onClear: () => apply({ scope: null }),
      control: (close) => (
        <>
          <Select
            style={{ width: "100%" }}
            placeholder="雇用範囲を選ぶ"
            value={filters.scope}
            options={[
              { value: "all", label: "すべて(協力会社を含む)" },
              { value: "direct", label: "直雇用のみ" },
            ]}
            onChange={(v) => {
              apply({ scope: v === "direct" ? "direct" : null });
              close();
            }}
          />
          <PopClose onClick={close} />
        </>
      ),
    },
    {
      key: "needs_review",
      label: "要確認",
      value: filters.needs_review ? "氏名が要確認" : "",
      onClear: () => apply({ needs_review: null }),
      control: (close) => (
        <>
          <Checkbox
            checked={filters.needs_review}
            onChange={(e) => apply({ needs_review: e.target.checked ? "1" : null })}
          >
            氏名が要確認(名が空)の行だけ
          </Checkbox>
          <PopClose onClick={close} />
        </>
      ),
    },
    {
      key: "status",
      label: "在籍",
      value: filters.status ? statusLabel(filters.status) : "",
      onClear: () => apply({ status: null }),
      control: (close) => (
        <>
          <Select
            style={{ width: "100%" }}
            placeholder="在籍状態を選ぶ"
            value={filters.status || undefined}
            options={STATUS_FILTERS.map((s) => ({ value: s.value, label: s.label }))}
            onChange={(v) => {
              apply({ status: v });
              close();
            }}
          />
          <PopClose onClick={close} />
        </>
      ),
    },
    {
      key: "org",
      label: "所属",
      value: filters.org_unit_id
        ? `${unitName(filters.org_unit_id)}${filters.include_descendants ? "(配下を含む)" : ""}`
        : "",
      onClear: () => apply({ org_unit_id: null, include_descendants: null }),
      control: (close) => (
        <>
          <TreeSelect
            style={{ width: "100%" }}
            allowClear
            treeDefaultExpandAll
            placeholder="組織を選ぶ"
            treeData={unitOptions(units.data?.items ?? [])}
            value={filters.org_unit_id}
            onChange={(v) => apply({ org_unit_id: v ? String(v) : null })}
          />
          <Checkbox
            checked={filters.include_descendants}
            onChange={(e) => apply({ include_descendants: e.target.checked ? null : "0" })}
          >
            配下も含める
          </Checkbox>
          <PopClose onClick={close} />
        </>
      ),
    },
  ];

  const columns: DataColumn<Employee>[] = [
    {
      title: "社員番号",
      ellipsis: false,
      dataIndex: "emp_code",
      width: 110,
      // サーバー側で並べる(ページをまたいで効く)。昇順 → 降順 → 無し(= 社員番号順)
      sorter: true,
      sortOrder: filters.sort === "emp_code" ? (filters.order === "desc" ? "descend" : "ascend") : null,
      render: (v: string, row) => (
        <Link className="ui-num" to={`/hr/employees/${row.id}`}>
          {v}
        </Link>
      ),
    },
    {
      title: "氏名",
      key: "name",
      width: 280,
      ellipsis: false,
      // カナ(無ければ漢字)の姓名順。昇順 → 降順 → 無し(= 社員番号順)
      sorter: true,
      sortOrder: filters.sort === "name" ? (filters.order === "desc" ? "descend" : "ascend") : null,
      render: (_: unknown, row) => {
        const kana = [row.family_name_kana, row.given_name_kana].filter(Boolean).join(" ");
        return (
          <>
            <span className="ui-cell-main">
              {/* 行クリックと併存。キーボード(Tab → Enter)でもパネルを開けるように */}
              <button type="button" className="ui-link-btn" onClick={() => setPanelId(String(row.id))}>
                {row.name}
              </button>
              {row.name_needs_review && (
                <>
                  {" "}
                  <StatusTag tone="warning">要確認</StatusTag>
                </>
              )}
            </span>
            {kana && <span className="ui-cell-sub">{kana}</span>}
          </>
        );
      },
    },
    // 一覧/詳細テンプレートへの載せ替え(2026-10-03)で右パネルだけになっていた。一覧で見たい
    // (HIDE 2026-10-15)。未設定の協力会社メンバーも居るので空は "—"
    {
      title: "メール",
      dataIndex: "email",
      width: 240,
      ellipsis: false,
      render: (v: string) => v || "—",
    },
    { title: "所属", ellipsis: true, dataIndex: "org_unit_path", width: 320 },
    { title: "役職", ellipsis: false, dataIndex: "position_name", width: 160 },
    {
      title: "雇用区分",
      width: 180,
      ellipsis: false,
      render: (_: unknown, row) => (
        <>
          <span className="ui-cell-main">{row.employment_type || "—"}</span>
          {!row.direct && <span className="ui-cell-sub">直雇用ではない</span>}
        </>
      ),
    },
    {
      title: "状態",
      ellipsis: false,
      dataIndex: "status",
      width: 110,
      render: (v: string) => (
        <StatusTag tone={employeeStatusTone(v)} dot>
          {statusLabel(v)}
        </StatusTag>
      ),
    },
    {
      title: "入社日",
      ellipsis: false,
      dataIndex: "joined_at",
      width: 120,
      render: (v: string | null) => <span className="ui-num">{v ?? "—"}</span>,
    },
    ...(showPhones
      ? ([
          { title: "会社携帯", ellipsis: false, dataIndex: "work_mobile", width: 160 },
          { title: "会社電話", ellipsis: false, dataIndex: "work_phone", width: 160 },
          { title: "内線", ellipsis: false, dataIndex: "extension", width: 110 },
        ] as DataColumn<Employee>[])
      : []),
    // カスタム項目のうち「一覧に出す」もの(人事 › 設定 › カスタム項目)
    ...listFields.map(
      (f): DataColumn<Employee> => ({
        title: f.label,
        ellipsis: /備考|説明|本文|詳細|理由|メッセージ|件名|URL|エラー|パス|メモ/i.test(f.label),
        onCell: (row) => ({ title: /備考|説明|本文|詳細|理由|メッセージ|件名|URL|エラー|パス|メモ/i.test(f.label) ? String(row.extra?.[f.key] ?? "") : undefined }),
        key: `extra.${f.key}`,
        width: Math.max(160, f.label.length * 14 + 32, /備考|説明|本文|詳細|理由|メッセージ|件名|URL|エラー|パス|メモ/i.test(f.label) ? 320 : /メール/.test(f.label) ? 240 : 0, ...f.options.map((value) => value.length * 14 + 32)),
        render: (_: unknown, row) => row.extra?.[f.key] ?? "",
      }),
    ),
  ];

  const total = query.data?.total ?? 0;
  const view = activeView(filters);

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="従業員一覧"
          description="在籍・所属・役職で絞り込み、行を押すと右に詳細が出る。"
          secondary={
            <Button onClick={() => void download(`/api/hr/employees.xlsx?${new URLSearchParams(filterParams(filters))}`)}>
              Excel で出力
            </Button>
          }
          primary={
            canWrite ? (
              <Button type="primary" onClick={() => setCreating(true)}>
                従業員を登録
              </Button>
            ) : undefined
          }
        />
        <FilterBar
          search={{
            value: qInput,
            onChange: setQInput,
            placeholder: "社員番号・氏名・メール・所属(空白区切りのAND)",
          }}
          views={{
            items: VIEWS.map((v) => ({
              key: v.key,
              label: v.label,
              count: counts[v.key],
              tone: v.tone,
            })),
            active: view,
            onChange: (key) => apply(VIEWS.find((v) => v.key === key)?.set ?? {}),
          }}
          conditions={conditions}
          right={
            <Space>
              {filtered && (
                <Button size="small" onClick={clearAll}>
                  条件を外す
                </Button>
              )}
              <Checkbox checked={showPhones} onChange={(e) => setShowPhones(e.target.checked)}>
                電話を表示
              </Checkbox>
            </Space>
          }
        />
        {query.isError && (
          <Alert type="error" showIcon message="従業員を取得できなかった" description={errorText(query.error)} />
        )}
        <DataTable<Employee>
          columns={columns}
          rows={query.data?.items ?? []}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          total={total}
          page={page}
          pageSize={pageSize}
          onPage={(p, s) => {
            setPage(p);
            setPageSize(s);
          }}
          selectedKey={panelId ? Number(panelId) : null}
          onRowClick={(row) => setPanelId(String(row.id))}
          onSort={(field, dir) =>
            apply(dir && (field === "emp_code" || field === "name") ? { sort: field, order: dir } : { sort: null, order: null })
          }
          error={query.isError ? "取得できなかったので表示できない" : undefined}
          rowSelection={
            contacts.enabled
              ? {
                  selectedRowKeys: selected,
                  onChange: (keys) => setSelected(keys.map(Number)),
                  // ページをまたいでも選択を残す(50 件ずつ・全 91 件で現実に起きる)
                  preserveSelectedRowKeys: true,
                  columnWidth: 40,
                }
              : undefined
          }
          barLeft={
            contacts.enabled ? (
              <Space wrap>
                <span style={{ color: "var(--ux-text-secondary)" }}>
                  電話帳の個別指定{selected.length ? `(${selected.length} 人)` : ""}:
                </span>
                <Button
                  size="small"
                  disabled={!selected.length}
                  loading={contacts.write.isPending}
                  onClick={() => contactAction(["distribute"], "include", "配布先に追加")}
                >
                  電話帳: 配布先に追加
                </Button>
                <Button
                  size="small"
                  disabled={!selected.length}
                  loading={contacts.write.isPending}
                  onClick={() => contactAction(["listing"], "include", "掲載に追加")}
                >
                  電話帳: 掲載に追加
                </Button>
                <Button
                  size="small"
                  danger
                  disabled={!selected.length}
                  loading={contacts.write.isPending}
                  onClick={() => contactAction(["distribute", "listing"], "exclude", "除外")}
                >
                  電話帳: 除外
                </Button>
              </Space>
            ) : null
          }
          empty={{
            text: filtered ? "この条件に合う従業員はいない" : "従業員はまだ登録されていない",
            action:
              canWrite && !filters.q ? (
                <Button type="primary" onClick={() => setCreating(true)}>
                  従業員を登録
                </Button>
              ) : undefined,
          }}
        />
      </div>
      {panelId && <EmployeePanel id={Number(panelId)} onClose={() => setPanelId(null)} />}
      <EmployeeFormModal open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}
